/**
 * Qué se puede hacer con un chat, y qué pasa cuando lo hacés. Una sola vez.
 *
 * POR QUÉ EXISTE (2026-08-22): el menú contextual de un chat —archivar, silenciar, fijar, vaciar,
 * bloquear— vivía entero dentro de la bandeja: la lista dibujaba las opciones y la página ejecutaba
 * las 117 líneas del `switch`. Cuando el founder pidió el mismo menú en el desplegable del
 * encabezado, la salida fácil era copiarlo.
 *
 * Copiarlo habría creado la SEGUNDA definición de «archivar un chat». Este repo ya pagó eso más de
 * una vez: dos sistemas de inventario que decían cosas distintas, y un mapa de canales duplicado en
 * `printOrder` que imprimía `mercadolibre` en crudo. Dos copias de una regla siempre terminan
 * diciendo cosas distintas, y la que nadie mira es la que miente.
 *
 * CÓMO SE REPARTE EL TRABAJO
 *   · `menuDeConversacion` arma las opciones — el mismo texto y los mismos íconos en todos lados.
 *   · `parcheOptimista` dice cómo se ve la conversación DESPUÉS, para pintarlo enseguida.
 *   · `persistirAccion` lo escribe y dice si salió bien; si no, quien llama revierte su parche.
 *
 * 🪤 EL PARCHE Y LA ESCRITURA ESTÁN SEPARADOS A PROPÓSITO. Cada pantalla guarda su lista en su
 * propio estado, así que la función no puede tocarla — pero el CÁLCULO de cómo queda sí es único.
 * Antes, tres de estas acciones eran optimistas sin revertir: si el UPDATE fallaba, el fijado se
 * veía aplicado y volvía atrás al refrescar.
 *
 * QUÉ NO ESTÁ ACÁ, y es a propósito: `label` abre un modal y `delete` tiene su propia confirmación
 * con consecuencias distintas en cada pantalla. Son decisiones de interfaz, no de dominio; quien
 * llama las resuelve.
 */
import { supabase } from '@/lib/supabase';
import { waAuditLog } from '@/lib/waAudit';
import {
  ArchiveIcon, NotificationIcon, PinIcon, PriceListIcon, EnvelopeIcon, DisabledIcon, DeleteIcon,
} from '@/lib/polaris-icons';

/** Lo mínimo que hay que saber de un chat para ofrecerle acciones. */
export interface ConversacionParaAccion {
  id: string;
  status?: string | null;
  is_pinned?: boolean | null;
  muted_until?: string | null;
  unread_count?: number | null;
}

export type AccionDeConversacion =
  | 'archive' | 'unarchive' | 'mute' | 'pin' | 'label' | 'unread' | 'block' | 'clear' | 'delete';

const estaSilenciado = (c: ConversacionParaAccion) =>
  c.muted_until ? new Date(c.muted_until) > new Date() : false;

/**
 * Las opciones del menú, en dos grupos: las de todos los días arriba y las que hacen daño abajo.
 * Los textos cambian según el estado —«Fijar» / «Desfijar»— porque un menú que no dice en qué
 * estado estás obliga a probar para averiguarlo.
 */
export function menuDeConversacion(
  conv: ConversacionParaAccion,
  alElegir: (accion: AccionDeConversacion) => void,
) {
  const fijado = !!conv.is_pinned;
  const silenciado = estaSilenciado(conv);
  const archivado = conv.status === 'archived';

  const principales = [
    archivado
      ? { content: 'Desarchivar chat', icon: ArchiveIcon, onAction: () => alElegir('unarchive') }
      : { content: 'Archivar chat', icon: ArchiveIcon, onAction: () => alElegir('archive') },
    {
      content: silenciado ? 'Reactivar notificaciones' : 'Silenciar notificaciones',
      icon: NotificationIcon,
      onAction: () => alElegir('mute'),
    },
    { content: fijado ? 'Desfijar chat' : 'Fijar chat', icon: PinIcon, onAction: () => alElegir('pin') },
    { content: 'Etiquetar chat', icon: PriceListIcon, onAction: () => alElegir('label') },
    { content: 'Marcar como no leído', icon: EnvelopeIcon, onAction: () => alElegir('unread') },
  ];

  const peligrosas = [
    {
      content: conv.status === 'blocked' ? 'Desbloquear' : 'Bloquear',
      icon: DisabledIcon,
      onAction: () => alElegir('block'),
    },
    { content: 'Vaciar chat', icon: DeleteIcon, onAction: () => alElegir('clear') },
    { content: 'Eliminar chat', icon: DeleteIcon, destructive: true as const, onAction: () => alElegir('delete') },
  ];

  return { principales, peligrosas };
}

/** Cuánto dura un silencio. Ocho horas: cubre una jornada sin volverse permanente por olvido. */
const HORAS_DE_SILENCIO = 8;

/**
 * Cómo queda la conversación si la acción sale bien. `null` = esta acción no cambia la fila (o la
 * saca de la lista, que quien llama resuelve porque depende de qué esté mostrando).
 */
export function parcheOptimista(
  conv: ConversacionParaAccion,
  accion: AccionDeConversacion,
): Partial<ConversacionParaAccion> | null {
  switch (accion) {
    case 'archive': return { status: 'archived' };
    case 'unarchive': return { status: 'open' };
    case 'unread': return { unread_count: Math.max(conv.unread_count ?? 0, 1) };
    case 'pin': return { is_pinned: !conv.is_pinned };
    case 'mute':
      return {
        muted_until: estaSilenciado(conv)
          ? null
          : new Date(Date.now() + HORAS_DE_SILENCIO * 3600_000).toISOString(),
      };
    default: return null;
  }
}

export interface ResultadoDeAccion {
  ok: boolean;
  /** Para contarlo, no para mostrarlo crudo. */
  error?: string;
}

/**
 * Escribe la acción. Devuelve si salió bien para que quien llama revierta su parche si no.
 *
 * 🪤 `block` NO es un UPDATE. Antes lo era, y el bloqueo quedaba cosmético: el contacto seguía
 * pudiendo escribir. Pasa por la edge function, que además lo propaga a WhatsApp de verdad.
 */
export async function persistirAccion(
  conv: ConversacionParaAccion,
  accion: AccionDeConversacion,
  contexto: { tenantId?: string | null; userId?: string | null },
): Promise<ResultadoDeAccion> {
  const ahora = new Date().toISOString();
  const { tenantId, userId } = contexto;

  const anotar = (evento: string) => {
    if (tenantId && userId) void waAuditLog(tenantId, conv.id, userId, evento);
  };

  // Archivar tampoco es sólo un UPDATE: el celular tiene que archivar el mismo chat (y al revés, por CHATS_ARCHIVE).
  if (accion === 'archive' || accion === 'unarchive') {
    const { error } = await supabase.functions.invoke('whatsapp-send-message', {
      body: { action: 'archive', conversation_id: conv.id, archive: accion === 'archive' },
    });
    if (error) return { ok: false, error: error.message };
    anotar(accion);
    return { ok: true };
  }

  if (accion === 'block') {
    const bloquear = conv.status !== 'blocked';
    const { error } = await supabase.functions.invoke('whatsapp-send-message', {
      body: { action: 'block', conversation_id: conv.id, block: bloquear },
    });
    if (error) return { ok: false, error: error.message };
    anotar(bloquear ? 'block' : 'unblock');
    return { ok: true };
  }

  if (accion === 'clear') {
    /*
     * 🪤 Los mensajes primero y el contador después, esperando los dos. Antes era fire-and-forget:
     * si el DELETE fallaba, igual se ponía `message_count = 0` y la conversación quedaba diciendo
     * «0 mensajes» con 800 mensajes reales adentro.
     */
    const borrado = supabase.from('wa_messages').delete().eq('conversation_id', conv.id);
    const { error: errBorrar } = await (tenantId ? borrado.eq('tenant_id', tenantId) : borrado);
    if (errBorrar) return { ok: false, error: errBorrar.message };

    /*
     * 🚨 `unread_count` TAMBIÉN, Y ÉSTE ES EL QUE SE VE.
     *
     * Se ponía `message_count = 0` y se olvidaba el globo verde. Los dos contadores los escribe el
     * mismo trigger (`wa_trg_message_inserted`: `message_count + 1` siempre, `unread_count + 1` sólo
     * si el mensaje es entrante) y no hay nada que reste al BORRAR. Así que vaciar el chat dejaba
     * «0 mensajes» y el globo intacto: en la bandeja, un chat vacío pidiendo que lo lean, y un
     * número que no se apaga hasta que alguien lo abre.
     *
     * Medido en producción el 01-sep-2026: 8 conversaciones con el globo por encima de sus mensajes
     * entrantes reales, 148 no leídos fantasma, y 3 mostrando número sobre cero mensajes.
     *
     * 🪤 VA UN CERO ABSOLUTO, NO UN `- 1`. Restar exigiría saber cuántos entrantes se borraron y
     * llegaría tarde ante un mensaje que entre en el medio; después de vaciar, lo que hay para leer
     * es cero por definición.
     */
    const { error: errContador } = await supabase.from('wa_conversations')
      .update({
        message_count: 0,
        unread_count: 0,
        last_message_body: null,
        last_message_type: null,
        updated_at: ahora,
      })
      .eq('id', conv.id);
    if (errContador) return { ok: false, error: errContador.message };
    anotar('clear');
    return { ok: true };
  }

  const parche = parcheOptimista(conv, accion);
  if (!parche) return { ok: true };

  const { error } = await supabase
    .from('wa_conversations')
    .update({ ...parche, updated_at: ahora })
    .eq('id', conv.id);
  if (error) return { ok: false, error: error.message };

  return { ok: true };
}

/** Lo que hay que preguntar antes, o `null` si la acción no necesita confirmación. */
export function confirmacionNecesaria(accion: AccionDeConversacion, nombre: string): string | null {
  switch (accion) {
    case 'clear':
      return '¿Vaciar todos los mensajes de este chat? Los mensajes se eliminarán permanentemente.';
    case 'block':
      return `¿Bloquear a ${nombre}? No recibirás más mensajes de este contacto.`;
    default:
      return null;
  }
}
